import { AssignWorkerSection } from '@/components/features/WorkOrders/components/AssignWorkerSection';
import { PhotoUpload } from '@/components/features/WorkOrders/components/PhotoUpload';
import { AddressMapCard } from '@/components/global/address/AddressMapCard';
import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import { CompanySelector } from '@/components/global/selectors/CompanySelector';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Separator } from '@/components/ui/shadcn/separator';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatDate } from '@/lib/utils';
import { type Company, type User } from '@/types';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

interface SettingOption {
    value: string;
    label: string;
    color?: string;
    icon?: string;
}

interface WorkOrderFormProps {
    users?: User[];
    companies?: Company[];
    statuses?: SettingOption[];
    priorities?: SettingOption[];
    types?: SettingOption[];
    onSubmit?: (data: FormData) => void;
    initialData?: Partial<FormData>;
    errors?: Record<string, string>;
}

const workOrderSchema = z
    .object({
        contact_first_name: z.string().min(1, 'First name is required').max(255),
        contact_last_name: z.string().min(1, 'Last name is required').max(255),
        phone: z.string().max(20).optional().or(z.literal('')),
        email: z.string().email('Invalid email address').max(255).optional().or(z.literal('')),
        service_address: z.string().optional(),
        description: z.string().min(1, 'Description is required'),
        status: z.string(),
        priority: z.string(),
        type: z.string(),
        company_id: z.string().min(1, 'Service company is required'),
        assigned_user_id: z.string().optional(),
        paying_company_id: z.string().optional(),
        estimated_date: z.string().optional(),
        assignments: z
            .array(
                z.object({
                    user_id: z.string(),
                    role: z.enum(['lead', 'helper']),
                }),
            )
            .optional(),
        service_address_lat: z.number().nullable().optional(),
        service_address_lng: z.number().nullable().optional(),
        photos: z.array(z.any()).optional(),
        assigned_user_roles: z.string().optional(),
        // Address fields used for backend processing
        service_address_street: z.string().optional(),
        service_address_house_number: z.string().optional(),
        service_address_postal_code: z.string().optional(),
        service_address_city: z.string().optional(),
        service_address_municipality: z.string().optional(),
        service_address_place_id: z.string().optional(),
    })
    .refine((data) => data.phone || data.email, {
        message: 'Either phone or email is required',
        path: ['email'], // Show error on email field
    });

type FormData = z.infer<typeof workOrderSchema>;

export function WorkOrderForm({
    users = [],
    companies = [],
    statuses = [],
    priorities = [],
    types = [],
    onSubmit,
    initialData,
    errors: backendErrors = {},
}: WorkOrderFormProps) {
    const { t } = useTranslations();
    const {
        register,
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors: formErrors },
    } = useForm<FormData>({
        resolver: standardSchemaResolver(workOrderSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        defaultValues: {
            contact_first_name: initialData?.contact_first_name || '',
            contact_last_name: initialData?.contact_last_name || '',
            phone: initialData?.phone || '',
            email: initialData?.email || '',
            service_address: initialData?.service_address || '',
            description: initialData?.description || '',
            status: initialData?.status || statuses[0]?.value || '',
            priority: initialData?.priority || priorities[0]?.value || '',
            type: initialData?.type || types[0]?.value || '',
            company_id: initialData?.company_id || '',
            assigned_user_id: initialData?.assigned_user_id || '',
            paying_company_id: initialData?.paying_company_id || '',
            estimated_date: initialData?.estimated_date || '',
            assignments: [],
            service_address_lat: initialData?.service_address_lat ?? null,
            service_address_lng: initialData?.service_address_lng ?? null,
            photos: [],
            assigned_user_roles: initialData?.assigned_user_roles || '',
            service_address_street: initialData?.service_address_street || '',
            service_address_house_number: initialData?.service_address_house_number || '',
            service_address_postal_code: initialData?.service_address_postal_code || '',
            service_address_city: initialData?.service_address_city || '',
            service_address_municipality: initialData?.service_address_municipality || '',
        },
    });

    const watchedEstimatedDate = watch('estimated_date');
    const watchedServiceAddress = watch('service_address');
    const watchedLat = watch('service_address_lat');
    const watchedLng = watch('service_address_lng');
    const watchedPhotos = watch('photos');

    const translatedStatuses = useMemo(() => {
        return statuses.map((status) => ({
            ...status,
            label: t(`settings.work_order_statuses.${status.value}`, status.label),
        }));
    }, [statuses, t]);

    const translatedPriorities = useMemo(() => {
        return priorities.map((priority) => ({
            ...priority,
            label: t(`settings.work_order_priorities.${priority.value}`, priority.label),
        }));
    }, [priorities, t]);

    const translatedTypes = useMemo(() => {
        return types.map((type) => {
            const translationKeyValue = type.value.trim().toLowerCase().replace(/\s+/g, '_');
            return {
                ...type,
                label: t(`settings.work_order_types.${translationKeyValue}`, type.label),
            };
        });
    }, [types, t]);

    const selectedDate = useMemo(() => {
        if (!watchedEstimatedDate) {
            return undefined;
        }
        const parsed = new Date(watchedEstimatedDate);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    }, [watchedEstimatedDate]);

    const handleFormSubmit = (data: FormData) => {
        // Only call onSubmit if validation passes
        // react-hook-form's handleSubmit already prevents submission on validation errors
        onSubmit?.(data);
    };

    const handleFormError = (errors: Record<string, { message?: string }>) => {
        // Validation failed - errors are already in formState.errors
        // This handler is called when validation fails
        // The errors will be automatically displayed via formState.errors
        console.log('Validation failed with errors:', errors);
    };

    // Helper to get error message (backend or frontend)
    const getErrorMessage = (field: keyof FormData) => {
        // Check form errors first (from Zod validation)
        const formError = formErrors[field];
        if (formError?.message) {
            return formError.message;
        }
        // Then check backend errors
        if (backendErrors[field]) {
            return backendErrors[field];
        }
        return undefined;
    };

    // Helper to check if field has error
    const hasError = (field: keyof FormData) => {
        return !!getErrorMessage(field);
    };

    return (
        <form onSubmit={handleSubmit(handleFormSubmit, handleFormError)} className="space-y-6" noValidate>
            {/* Contact Information */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('work_orders.forms.create.sections.contact_information', 'Contact Information')}</CardTitle>
                    <CardDescription>
                        {t('work_orders.forms.create.sections.contact_information_description', 'Primary contact details for this work order')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="contact_first_name">{t('work_orders.forms.create.labels.contact_first_name', 'First Name')} *</Label>
                            <Input
                                id="contact_first_name"
                                className="peer"
                                aria-invalid={hasError('contact_first_name')}
                                placeholder={t('work_orders.forms.create.placeholders.enter_first_name', 'Enter first name')}
                                {...register('contact_first_name')}
                            />
                            {getErrorMessage('contact_first_name') ? (
                                <p className="text-muted-foreground peer-aria-invalid:text-destructive text-xs">
                                    {getErrorMessage('contact_first_name')}
                                </p>
                            ) : null}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contact_last_name">{t('work_orders.forms.create.labels.contact_last_name', 'Last Name')} *</Label>
                            <Input
                                id="contact_last_name"
                                className="peer"
                                aria-invalid={hasError('contact_last_name')}
                                placeholder={t('work_orders.forms.create.placeholders.enter_last_name', 'Enter last name')}
                                {...register('contact_last_name')}
                            />
                            {getErrorMessage('contact_last_name') && (
                                <p className="text-muted-foreground peer-aria-invalid:text-destructive text-xs">
                                    {getErrorMessage('contact_last_name')}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="phone">{t('work_orders.forms.create.labels.phone', 'Phone')}</Label>
                            <Input
                                id="phone"
                                type="tel"
                                placeholder={t('work_orders.forms.create.placeholders.enter_phone', 'Enter phone number')}
                                className="peer"
                                aria-invalid={hasError('phone')}
                                {...register('phone')}
                            />
                            {getErrorMessage('phone') && (
                                <p className="text-muted-foreground peer-aria-invalid:text-destructive text-xs">{getErrorMessage('phone')}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">{t('work_orders.forms.create.labels.email', 'Email')}</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder={t('work_orders.forms.create.placeholders.enter_email', 'Enter email address')}
                                className="peer"
                                aria-invalid={hasError('email')}
                                {...register('email')}
                            />
                            {getErrorMessage('email') && (
                                <p className="text-muted-foreground peer-aria-invalid:text-destructive text-xs">{getErrorMessage('email')}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="company_id">{t('work_orders.forms.create.labels.company', 'Company')} *</Label>
                            <Controller
                                control={control}
                                name="company_id"
                                render={({ field }) => (
                                    <CompanySelector
                                        value={field.value}
                                        onValueChange={(next) => field.onChange(next ?? '')}
                                        labels={{ placeholder: t('work_orders.forms.create.placeholders.select_company', 'Select company...') }}
                                        seedCompanies={companies}
                                    />
                                )}
                            />
                            {getErrorMessage('company_id') && (
                                <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('company_id')}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="paying_company_id">{t('work_orders.forms.create.labels.paying_company', 'Paying Company')}</Label>
                            <Controller
                                control={control}
                                name="paying_company_id"
                                render={({ field }) => (
                                    <CompanySelector
                                        value={field.value || ''}
                                        onValueChange={(next) => field.onChange(next ?? '')}
                                        labels={{
                                            placeholder: t('work_orders.forms.create.placeholders.select_paying_company', 'Select paying company...'),
                                        }}
                                        seedCompanies={companies}
                                    />
                                )}
                            />
                            {getErrorMessage('paying_company_id') && (
                                <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('paying_company_id')}</p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Service Details */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('work_orders.forms.create.sections.work_details', 'Work Details')}</CardTitle>
                    <CardDescription>
                        {t('work_orders.forms.create.sections.work_details_description', 'Where and what service is needed')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <GoogleMapsProvider>
                        <div className="grid grid-cols-1 gap-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="service_address">
                                        {t('work_orders.forms.create.labels.service_address', 'Service Address')} *
                                    </Label>
                                    <Controller
                                        control={control}
                                        name="service_address"
                                        render={({ field }) => (
                                            <AddressSearchInput
                                                value={field.value || ''}
                                                onChange={(value) => {
                                                    field.onChange(value);
                                                    setValue('service_address_lat', null);
                                                    setValue('service_address_lng', null);
                                                }}
                                                onAddressSelect={(address) => {
                                                    field.onChange(address.label);
                                                    setValue('service_address_lat', address.lat ?? null);
                                                    setValue('service_address_lng', address.lng ?? null);

                                                    // Set structured address fields
                                                    if (address.street) setValue('service_address_street', address.street);
                                                    if (address.house_number) setValue('service_address_house_number', address.house_number);
                                                    if (address.city) setValue('service_address_city', address.city);
                                                    // AddressSuggestion doesn't have municipality, use city or skip
                                                    // if (address.municipality) setValue('service_address_municipality', address.municipality);
                                                    if (address.postal_code) setValue('service_address_postal_code', address.postal_code);
                                                    if (address.id) setValue('service_address_place_id', address.id);
                                                }}
                                                placeholder={t(
                                                    'work_orders.edit_work_order.placeholders.enter_address',
                                                    'Enter or search for address...',
                                                )}
                                            />
                                        )}
                                    />
                                    {getErrorMessage('service_address') && (
                                        <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('service_address')}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">{t('work_orders.forms.create.labels.description', 'Work Description')} *</Label>
                                    <Textarea
                                        id="description"
                                        rows={4}
                                        placeholder={t(
                                            'work_orders.forms.create.placeholders.enter_description',
                                            'Describe the work to be performed...',
                                        )}
                                        className="peer"
                                        aria-invalid={hasError('description')}
                                        {...register('description')}
                                    />
                                    {getErrorMessage('description') && (
                                        <p className="text-muted-foreground peer-aria-invalid:text-destructive text-xs">
                                            {getErrorMessage('description')}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="h-full">
                                <AddressMapCard address={watchedServiceAddress || ''} lat={watchedLat} lng={watchedLng} className="h-full" />
                            </div>
                        </div>
                    </GoogleMapsProvider>
                </CardContent>
            </Card>

            {/* Work Order Settings */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('work_orders.forms.create.sections.work_order_settings', 'Work Order Settings')}</CardTitle>
                    <CardDescription>
                        {t('work_orders.forms.create.sections.work_order_settings_description', 'Status, priority, and assignment')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                            <Label htmlFor="status">{t('work_orders.edit_work_order.labels.status', 'Status')}</Label>
                            <Controller
                                control={control}
                                name="status"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger id="status" className="w-full">
                                            <SelectValue placeholder={t('work_orders.date_status.select_status', 'Select status')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {translatedStatuses.map((option) => (
                                                <SelectItem key={option.value} value={option.value} className="flex items-center gap-2">
                                                    {option.color && (
                                                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: option.color }} />
                                                    )}
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {getErrorMessage('status') && (
                                <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('status')}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="priority">{t('work_orders.edit_work_order.labels.priority', 'Priority')}</Label>
                            <Controller
                                control={control}
                                name="priority"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger id="priority" className="w-full">
                                            <SelectValue
                                                placeholder={t('work_orders.forms.create.placeholders.select_priority', 'Select priority...')}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {translatedPriorities.map((option) => (
                                                <SelectItem key={option.value} value={option.value} className="flex items-center gap-2">
                                                    {option.color && (
                                                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: option.color }} />
                                                    )}
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {getErrorMessage('priority') && (
                                <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('priority')}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="type">{t('work_orders.edit_work_order.labels.work_type', 'Type')}</Label>
                            <Controller
                                control={control}
                                name="type"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger id="type" className="w-full">
                                            <SelectValue
                                                placeholder={t('work_orders.forms.create.placeholders.select_work_type', 'Select work type...')}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {translatedTypes.map((option) => (
                                                <SelectItem key={option.value} value={option.value} className="flex items-center gap-2">
                                                    {option.color && (
                                                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: option.color }} />
                                                    )}
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {getErrorMessage('type') && <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('type')}</p>}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="estimated_date">{t('work_orders.edit_work_order.labels.estimated_date', 'Estimated Date')}</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn('w-full justify-start text-left font-normal', !selectedDate && 'text-muted-foreground')}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {selectedDate
                                        ? formatDate(selectedDate)
                                        : t('work_orders.edit_work_order.placeholders.pick_a_date', 'Pick a date')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => {
                                        setValue('estimated_date', date ? formatDate(date) : '');
                                    }}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                        {getErrorMessage('estimated_date') && (
                            <p className="text-muted-foreground text-destructive text-xs">{getErrorMessage('estimated_date')}</p>
                        )}
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                            <AssignWorkerSection
                                users={users}
                                onAssign={(assignments) => {
                                    // Backend expects a single UUID for assigned_user_id in store method
                                    // We take the lead or the first assigned user
                                    const lead = assignments.find((a) => a.role === 'lead') || assignments[0];
                                    setValue('assigned_user_id', lead ? lead.userId : '');
                                    setValue('assigned_user_roles', assignments.map((a) => a.role).join(','));
                                    setValue(
                                        'assignments',
                                        assignments.map((assignment) => ({
                                            user_id: assignment.userId,
                                            role: assignment.role,
                                        })),
                                    );
                                }}
                                noTitle={true}
                                autoSave
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Photos */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('work_orders.forms.create.photos.title', 'Photos (Optional)')}</CardTitle>
                    <CardDescription>
                        {t('work_orders.forms.create.photos.description', 'Upload photos to help describe the work needed')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <PhotoUpload photos={(watchedPhotos || []) as File[]} onPhotosChange={(photos) => setValue('photos', photos)} />
                </CardContent>
            </Card>

            <Separator />

            {/* Form Actions */}
            <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">
                <Button type="button" variant="outline" className="w-full sm:w-auto">
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" className="w-full sm:w-auto">
                    {t('work_orders.forms.create.buttons.create_work_order', 'Create Work Order')}
                </Button>
            </div>
        </form>
    );
}
